import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { DashboardLayout, WelcomeCard, RecentActivityCard } from './DashboardLayout';
import { dashboardService } from '@/services/dashboardService';
import type { 
  AgentDashboardStats, 
  DashboardActivity, 
  AssignedTicket, 
  QueueStatus,
  AgentPerformanceWeek
} from '@/services/dashboardService';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * Agent Dashboard Component
 * 
 * Provides ticket management interface for support agents including
 * assigned tickets, queue status, and productivity metrics.
 */
export function AgentDashboard() {
  useDocumentTitle('Agent Dashboard | SupportDesk');
  const { user } = useAuth();
  const [stats, setStats] = useState<any[]>([]);
  const [recentActivities, setRecentActivities] = useState<DashboardActivity[]>([]);
  const [assignedTickets, setAssignedTickets] = useState<AssignedTicket[]>([]);
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null);
  const [weeklyPerformance, setWeeklyPerformance] = useState<AgentPerformanceWeek | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [user?.id]);

  const loadDashboardData = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Load all agent dashboard data in parallel
      const [
        statsResponse, 
        activitiesResponse, 
        ticketsResponse, 
        queueResponse,
        performanceResponse
      ] = await Promise.all([
        dashboardService.getAgentStats(user.id),
        dashboardService.getRecentActivities('Agent', user.id),
        dashboardService.getAssignedTickets(user.id),
        dashboardService.getQueueStatus(),
        dashboardService.getAgentPerformanceWeek(user.id)
      ]);

      // Process agent stats
      if (statsResponse.success && statsResponse.data) {
        const data = statsResponse.data;
        const formattedStats = [
          {
            label: 'Total Tickets',
            value: data.totalTickets ? data.totalTickets.toLocaleString() : '0',
            change: data.changes?.totalTickets,
            href: '/tickets',
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 00-2 2v2a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2" />
              </svg>
            ),
          },
          {
            label: 'My Open Tickets',
            value: data.myOpenTickets.toString(),
            change: data.changes?.myOpenTickets,
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            ),
          },
          {
            label: 'Resolved Today',
            value: data.resolvedToday.toString(),
            change: data.changes?.resolvedToday,
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
          },
          {
            label: 'Avg Response Time',
            value: data.avgResponseTime,
            change: data.changes?.avgResponseTime,
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
          },
          {
            label: 'Customer Rating',
            value: data.customerRating,
            change: data.changes?.customerRating,
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            ),
          },
        ];
        setStats(formattedStats);
      }

      // Process activities
      if (activitiesResponse.success) {
        setRecentActivities(activitiesResponse.data || []);
      }

      // Process assigned tickets
      if (ticketsResponse.success) {
        setAssignedTickets(ticketsResponse.data || []);
      }

      // Process queue status
      if (queueResponse.success) {
        setQueueStatus(queueResponse.data || null);
      }

      // Process weekly performance
      if (performanceResponse.success) {
        setWeeklyPerformance(performanceResponse.data || null);
      }

    } catch (err) {
      console.error('Failed to load agent dashboard data:', err);
      setError('Failed to load dashboard data. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const actions = (
    <div className="flex space-x-3">
      <Link to="/tickets/new">
        <Button variant="outline">
          Create Ticket
        </Button>
      </Link>
      <Link to="/tickets">
        <Button variant="primary">
          View All Tickets
        </Button>
      </Link>
    </div>
  );

  const getPriorityBadge = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return <Badge variant="error" size="sm">High</Badge>;
      case 'medium':
        return <Badge variant="warning" size="sm">Medium</Badge>;
      case 'low':
        return <Badge variant="secondary" size="sm">Low</Badge>;
      default:
        return <Badge variant="secondary" size="sm">{priority}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return <Badge variant="info" size="sm">Open</Badge>;
      case 'inprogress':
        return <Badge variant="warning" size="sm">In Progress</Badge>;
      case 'resolved':
        return <Badge variant="success" size="sm">Resolved</Badge>;
      default:
        return <Badge variant="secondary" size="sm">{status}</Badge>;
    }
  };

  const getSlaStatusIcon = (slaStatus: string) => {
    switch (slaStatus) {
      case 'on-track':
        return <div className="h-2 w-2 rounded-full bg-green-400"></div>;
      case 'at-risk':
        return <div className="h-2 w-2 rounded-full bg-yellow-400"></div>;
      case 'breached':
        return <div className="h-2 w-2 rounded-full bg-red-400"></div>;
      default:
        return <div className="h-2 w-2 rounded-full bg-gray-400"></div>;
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  if (isLoading) {
    return (
      <DashboardLayout
        title="Agent Dashboard"
        subtitle="Manage your assigned tickets and help customers"
        stats={[]}
        actions={actions}
      >
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout
        title="Agent Dashboard"
        subtitle="Manage your assigned tickets and help customers"
        stats={[]}
        actions={actions}
      >
        <Card title="Error">
          <div className="p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadDashboardData}>Retry</Button>
          </div>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Agent Dashboard"
      subtitle="Manage your assigned tickets and help customers"
      stats={stats}
      actions={actions}
    >
      {/* Welcome Section */}
      <WelcomeCard user={{ firstName: user.firstName, role: user.role }} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* My Assigned Tickets */}
        <div className="lg:col-span-2">
          <Card title="My Assigned Tickets">
            <div className="p-6">
              {assignedTickets.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">No tickets assigned to you</p>
                  <Link
                    to="/tickets"
                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-600 bg-primary-100 hover:bg-primary-200"
                  >
                    Browse available tickets
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {assignedTickets.map((ticket) => (
                    <div key={ticket.id} className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            {getSlaStatusIcon(ticket.slaStatus)}
                            <Link
                              to={`/tickets/${ticket.ticketNumber || ticket.id}`}
                              className="text-sm font-medium text-gray-900 hover:text-primary-600 truncate"
                            >
                              {ticket.subject}
                            </Link>
                          </div>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span>#{ticket.ticketNumber || ticket.id}</span>
                            <span>•</span>
                            <span>{ticket.customer}</span>
                            <span>•</span>
                            <span>{ticket.lastUpdate}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {getPriorityBadge(ticket.priority)}
                          {getStatusBadge(ticket.status)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
                <Link
                  to="/tickets?assigned=me"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  View all my tickets →
                </Link>
                <Link
                  to="/tickets?status=unassigned"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  Browse unassigned tickets →
                </Link>
              </div>
            </div>
          </Card>
        </div>

        {/* Queue Status */}
        <Card title="Queue Status">
          <div className="p-6">
            {queueStatus ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-4 w-4 rounded bg-red-500"></div>
                    <span className="text-sm font-medium text-gray-900">Urgent</span>
                  </div>
                  <span className="text-sm text-gray-900 font-medium">{queueStatus.urgent} tickets</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-4 w-4 rounded bg-orange-500"></div>
                    <span className="text-sm font-medium text-gray-900">High</span>
                  </div>
                  <span className="text-sm text-gray-900 font-medium">{queueStatus.high} tickets</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-4 w-4 rounded bg-yellow-500"></div>
                    <span className="text-sm font-medium text-gray-900">Medium</span>
                  </div>
                  <span className="text-sm text-gray-900 font-medium">{queueStatus.medium} tickets</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-4 w-4 rounded bg-green-500"></div>
                    <span className="text-sm font-medium text-gray-900">Low</span>
                  </div>
                  <span className="text-sm text-gray-900 font-medium">{queueStatus.low} tickets</span>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-3">Total unassigned</p>
                    <p className="text-2xl font-bold text-gray-900">{queueStatus.totalUnassigned}</p>
                    <Link
                      to="/tickets?status=unassigned"
                      className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-500"
                    >
                      Pick up tickets →
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500">Loading queue status...</div>
            )}
          </div>
        </Card>

        {/* Recent Activity */}
        <RecentActivityCard 
          activities={recentActivities}
          title="My Recent Activity"
          emptyMessage="No recent activity"
        />

        {/* Performance This Week */}
        <Card title="This Week's Performance">
          <div className="p-6">
            {weeklyPerformance ? (
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-900">Tickets Resolved</span>
                    <span className="text-gray-500">{weeklyPerformance.ticketsResolved} this week</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-900">Avg Response Time</span>
                    <span className="text-gray-500">{weeklyPerformance.avgResponseTime}</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-gray-900">Customer Satisfaction</span>
                    <span className="text-gray-500">{weeklyPerformance.customerSatisfaction.toFixed(1)} / {weeklyPerformance.satisfactionScale.toFixed(1)}</span>
                  </div>
                  <div className="bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${(weeklyPerformance.customerSatisfaction / weeklyPerformance.satisfactionScale) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500">Loading performance data...</div>
            )}
          </div>
        </Card>

        {/* Quick Actions */}
        <Card title="Quick Actions">
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <Link
                to="/tickets/new"
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="text-center">
                  <div className="mx-auto h-8 w-8 text-primary-600 mb-2">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-900">New Ticket</span>
                </div>
              </Link>

              <Link
                to="/knowledge-base"
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="text-center">
                  <div className="mx-auto h-8 w-8 text-primary-600 mb-2">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-900">Knowledge Base</span>
                </div>
              </Link>

              <Link
                to="/customers"
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="text-center">
                  <div className="mx-auto h-8 w-8 text-primary-600 mb-2">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-900">Customers</span>
                </div>
              </Link>

              <Link
                to="/reports"
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="text-center">
                  <div className="mx-auto h-8 w-8 text-primary-600 mb-2">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-900">My Reports</span>
                </div>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}